import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wallet, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { useToast } from '../hooks/use-toast';
import { getAssetById } from '../data/mockAssets';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoanApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [asset, setAsset] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [cryptoType, setCryptoType] = useState('BTC');
  const [cryptoAmount, setCryptoAmount] = useState(1);
  const [ltvRatio, setLtvRatio] = useState(50);
  const [btcPrice, setBtcPrice] = useState(94707);
  const [ethPrice, setEthPrice] = useState(3245.67);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  useEffect(() => {
    const foundAsset = getAssetById(id);
    setAsset(foundAsset);
    fetchCryptoPrices();
  }, [id]);

  const fetchCryptoPrices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crypto/prices`);
      if (response.data.success) {
        setBtcPrice(response.data.prices.BTC.price);
        setEthPrice(response.data.prices.ETH.price);
      }
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const currentPrice = cryptoType === 'BTC' ? btcPrice : ethPrice;
  const collateralValue = cryptoAmount * currentPrice;
  const loanAmount = (collateralValue * ltvRatio) / 100;
  const maxLoanForAsset = asset ? asset.estimatedValue * 0.7 : 0;
  const canProceed = loanAmount >= (asset?.estimatedValue * 0.3); // At least 30% down payment

  const handleConnectWallet = async () => {
    setLoading(true);
    try {
      // In production, this would integrate with Coinbase Wallet SDK
      // For now, we'll simulate wallet connection
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockAddress = `0x${Math.random().toString(16).substr(2, 40)}`;
      setWalletAddress(mockAddress);
      setWalletConnected(true);
      
      toast({
        title: 'Wallet Connected',
        description: 'Your Coinbase wallet has been connected successfully.',
      });
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: 'Failed to connect wallet. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      
      const response = await axios.post(`${BACKEND_URL}/api/loans/apply`, {
        userId: user._id,
        assetId: asset.id,
        assetName: asset.name,
        assetValue: asset.estimatedValue,
        cryptoType,
        cryptoAmount,
        collateralValue,
        ltvRatio,
        loanAmount,
        walletAddress
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast({
          title: 'Application Submitted',
          description: 'Your loan application has been submitted successfully!',
        });
        navigate('/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.message || 'Failed to submit application.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!asset) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a1628] to-[#0f1f3a] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e293b] to-[#0f172a]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Apply for Loan</h1>
            <p className="text-gray-400">Complete your application to acquire {asset.name}</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                1
              </div>
              <div className={`h-1 w-20 ${
                step >= 2 ? 'bg-blue-500' : 'bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                2
              </div>
              <div className={`h-1 w-20 ${
                step >= 3 ? 'bg-blue-500' : 'bg-gray-700'
              }`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>
                3
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <div>
              {step === 1 && (
                <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Collateral Configuration</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Select Cryptocurrency</label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => setCryptoType('BTC')}
                          className={`p-4 rounded-lg border transition-all ${
                            cryptoType === 'BTC'
                              ? 'border-blue-500 bg-blue-500/20 text-white'
                              : 'border-blue-500/30 text-gray-400 hover:border-blue-400'
                          }`}
                        >
                          <div className="font-semibold">Bitcoin (BTC)</div>
                          <div className="text-sm">${btcPrice.toLocaleString()}</div>
                        </button>
                        <button
                          onClick={() => setCryptoType('ETH')}
                          className={`p-4 rounded-lg border transition-all ${
                            cryptoType === 'ETH'
                              ? 'border-blue-500 bg-blue-500/20 text-white'
                              : 'border-blue-500/30 text-gray-400 hover:border-blue-400'
                          }`}
                        >
                          <div className="font-semibold">Ethereum (ETH)</div>
                          <div className="text-sm">${ethPrice.toLocaleString()}</div>
                        </button>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-gray-300">Amount of {cryptoType}</label>
                        <span className="text-white font-semibold">{cryptoAmount} {cryptoType}</span>
                      </div>
                      <Slider 
                        value={[cryptoAmount]} 
                        onValueChange={(value) => setCryptoAmount(value[0])}
                        min={0.1}
                        max={1000}
                        step={0.5}
                        className="my-4"
                      />
                      <div className="text-sm text-gray-400">
                        Collateral Value: ${collateralValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-gray-300">Loan-to-Value Ratio</label>
                        <span className="text-white font-semibold">{ltvRatio}%</span>
                      </div>
                      <Slider 
                        value={[ltvRatio]} 
                        onValueChange={(value) => setLtvRatio(value[0])}
                        min={20}
                        max={150}
                        step={5}
                        className="my-4"
                      />
                      <div className="text-sm text-gray-400">
                        Loan Amount: ${loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>

                    {!canProceed && (
                      <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-yellow-200">
                          Your loan amount must cover at least 30% of the asset value (${(asset.estimatedValue * 0.3).toLocaleString()}) to proceed.
                        </div>
                      </div>
                    )}

                    <Button 
                      onClick={() => setStep(2)}
                      disabled={!canProceed}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      Continue to Wallet Connection
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Connect Wallet</h2>
                  
                  {!walletConnected ? (
                    <div className="space-y-6">
                      <div className="text-center py-8">
                        <Wallet className="h-20 w-20 text-blue-400 mx-auto mb-4" />
                        <p className="text-gray-300 mb-6">
                          Connect your Coinbase wallet to deposit your {cryptoType} collateral
                        </p>
                        <Button 
                          onClick={handleConnectWallet}
                          disabled={loading}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6"
                        >
                          {loading ? 'Connecting...' : 'Connect Coinbase Wallet'}
                        </Button>
                      </div>
                      
                      <div className="flex gap-4">
                        <Button 
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                        >
                          Back
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Wallet className="h-6 w-6 text-green-400 mr-3" />
                          <span className="text-green-400 font-semibold">Wallet Connected</span>
                        </div>
                        <div className="text-sm text-gray-300">
                          Address: <span className="text-white font-mono">{walletAddress.slice(0, 10)}...{walletAddress.slice(-8)}</span>
                        </div>
                      </div>

                      <div className="bg-blue-900/20 rounded-lg p-4">
                        <p className="text-gray-300 text-sm">
                          Your {cryptoAmount} {cryptoType} will be securely held as collateral. You'll maintain ownership and can reclaim it after loan repayment.
                        </p>
                      </div>

                      <div className="flex gap-4">
                        <Button 
                          onClick={() => setStep(1)}
                          variant="outline"
                          className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                        >
                          Back
                        </Button>
                        <Button 
                          onClick={() => setStep(3)}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                        >
                          Review Application
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-8">
                  <h2 className="text-2xl font-bold text-white mb-6">Review & Submit</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Asset</span>
                      <span className="text-white font-semibold">{asset.name}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Asset Value</span>
                      <span className="text-white font-semibold">${asset.estimatedValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Collateral</span>
                      <span className="text-white font-semibold">{cryptoAmount} {cryptoType}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">Collateral Value</span>
                      <span className="text-white font-semibold">${collateralValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-blue-500/30">
                      <span className="text-gray-400">LTV Ratio</span>
                      <span className="text-white font-semibold">{ltvRatio}%</span>
                    </div>
                    <div className="flex justify-between py-3 text-lg font-bold">
                      <span className="text-white">Loan Amount</span>
                      <span className="text-blue-400">${loanAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>

                  <div className="bg-blue-900/20 rounded-lg p-4 mb-6">
                    <p className="text-gray-300 text-sm">
                      By submitting this application, you agree to our terms and conditions. Your collateral will be held securely until loan repayment.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      onClick={() => setStep(2)}
                      variant="outline"
                      className="flex-1 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white py-6"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={handleSubmitApplication}
                      disabled={loading}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Asset Summary */}
            <div className="bg-[#0f1f3a] border border-blue-500/30 rounded-lg p-8 h-fit">
              <h3 className="text-xl font-bold text-white mb-4">Asset Summary</h3>
              <img 
                src={asset.image} 
                alt={asset.name}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <h4 className="text-lg font-semibold text-white mb-2">{asset.name}</h4>
              <p className="text-gray-400 text-sm mb-4">{asset.location}</p>
              <div className="space-y-2 border-t border-blue-500/30 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Asset Value</span>
                  <span className="text-white font-semibold">${asset.estimatedValue.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Max Loan (70%)</span>
                  <span className="text-white font-semibold">${maxLoanForAsset.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 text-sm">Interest Rate</span>
                  <span className="text-white font-semibold">9.95%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoanApplication;
